export default ({content = '', conversationId = null, qtyVisibleItems = null, gap = null}) => ({
    onlineUsers: [],
    content: content,
    medias: [],
    selectedMedia: null,
    uploadType: null,
    showPrevBtn: false,
    showNextBtn: false,
    sending: false,
    qtyVisibleItems,
    gap,
    get isAllMediaUploaded() {
        return this.medias.every(media => media.uploaded);
    },
    get uploadTypeIsImage() {
        return this.uploadType === 'image';
    },
    get mediaIsFilled() {
        return this.medias.length > 0;
    },
    get selectedMediaIsImage() {
        return this.selectedMedia && this.selectedMedia.type === 'photo';
    },
    get acceptAttr() {
        return this.uploadType === 'video' ? 'video/*' : this.imageMimeTypes.join(',');
    },
    get imageMimeTypes() {
        return ['image/jpeg', 'image/gif', 'image/png'];
    },
    get contentIsFilled() {
        const content = this.content.trim();

        return content.replace(/(\r\n|\n|\r)/gm, '').length > 0;
    },
    errorToast(message = 'The content does not have a valid mime type or is bigger than 10MB.') {
        window.Livewire.dispatch(
            'toast::show',
            {type: 'error', message}
        );
    },
    handleInput(el) {
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;

        this.$nextTick(() => {
            this.$dispatch('chat-new-break-line-added');
        })
    },
    handleKeydown(event) {
        if (event.key === 'Enter' && !event.shiftKey) {
            if (this.contentIsFilled) {
                this.$wire.call('sendMessage');

                this.$dispatch('chat-message-sent');
            }
            return;
        }

        event.preventDefault();

        this.content += '\n';

        this.$nextTick(() => {
            event.target.style.height = 'auto';
            event.target.style.height = `${event.target.scrollHeight}px`;
            event.target.scrollTo(0, event.target.scrollHeight);

            const sidePanel = document.querySelector('.side-panel-container');
            sidePanel.scrollTo(0, sidePanel.scrollHeight);

            this.$dispatch('chat-new-break-line-added');
        });
    },
    fileSizeExceeded(size) {
        const maxSizeInMB = 10;

        return size > (maxSizeInMB * 1024 * 1024);
    },
    filterValidFiles(file) {
        if (this.fileSizeExceeded(file.size)) {
            return false;
        }

        if (this.uploadTypeIsImage && !this.imageMimeTypes.includes(file.type)) {
            return false;
        }

        return !(!this.uploadTypeIsImage && this.imageMimeTypes.includes(file.type));
    },
    uploadMedia(uploadType) {
        if (this.sending || this.$refs.mediaInput.disabled) {
            return;
        }

        this.uploadType = uploadType;

        this.$nextTick(() => {
            this.$refs.mediaInput.click();
        });
    },
    makeMedia(file) {
        return {
            id: Math.random().toString(36).slice(2),
            type: this.imageMimeTypes.includes(file.type) ? 'photo' : 'video',
            preview: URL.createObjectURL(file),
            progress: 0,
            uploaded: false,
            uploading: false,
            abortController: new AbortController(),
            file
        }
    },
    handleFileUpload(event, type) {
        if (this.sending || this.$refs.mediaInput.disabled) {
            return;
        }

        const files = [...event.target.files];

        if (files.length < 1) {
            return;
        }

        const mappedMedias = files
            .filter(this.filterValidFiles.bind(this))
            .map(this.makeMedia.bind(this));

        if (mappedMedias.length < 1) {
            this.errorToast();
            return;
        }

        if (mappedMedias.length !== files.length) {
            this.errorToast('Some media were rejected for invalid mime types or size over 10MB.');
        }

        this.$dispatch('toggle-persistent-overlay');

        this.medias = [...this.medias, ...mappedMedias];

        this.medias.forEach((media, index) => {
            media.index = index;
        })

        this.$refs.mediaInput.value = '';

        this.medias
            .filter((media) => {
                return media.uploaded === false && media.uploading === false;
            })
            .forEach((media, index) => {
                const formData = new FormData();
                formData.append('type', media.type);
                formData.append('media', media.file);

                axios.post(`/upload/media/temporarily`, formData, {
                    signal: media.abortController.signal,
                    onUploadProgress(event) {
                        media.uploading = true;
                        media.progress = Math.round(event.loaded * 100) / event.total;
                    }
                }).then(({data: response}) => {
                    this.handleSuccessUpload(media, response)
                }).catch(error => {
                    if (media.abortController.signal.aborted) {
                        return;
                    }

                    if (error.response.status === 422) {
                        this.errorToast();
                    }

                    this.removeMedia(media, index);
                }).finally(() => {
                    this.$dispatch('toggle-persistent-overlay');
                })
            });

        this.selectedMedia = this.medias[0];

        this.$nextTick(() => {
            this.checkVisibility();
        });
    },
    handleSuccessUpload(media, response) {
        media.progress = 100;
        media.uploaded = true;
        media.uploading = false;
        media.path = response.data.path;
    },
    cancelUpload(media) {
        media.abortController.abort()
    },
    removeMedia(media, index) {
        this.medias = this.medias.filter(({id}) => id !== media.id);

        let indexCandidate = 0;

        if (index > 0 && this.medias[index - 1]) {
            indexCandidate = index - 1;
        }

        this.selectedMedia = this.medias[indexCandidate] || null;

        if (!media.uploaded) {
            this.cancelUpload(media);
        }

        if (this.selectedMedia && this.selectedMedia.type === 'video') {
            this.recreateVideoElement();
        }

        this.$nextTick(() => {
            this.checkVisibility();
        });
    },
    selectMedia(index) {
        this.selectedMedia = this.medias[index];

        if (this.selectedMedia.type === 'video') {
            this.recreateVideoElement();
        }
    },
    recreateVideoElement() {
        this.$nextTick(() => {
            const videoElement = document.createElement('video');
            videoElement.setAttribute('controls', '');
            videoElement.classList.add('h-full', 'object-contain', 'w-full');
            videoElement.setAttribute('id', 'video-preview');

            const sourceElement = document.createElement('source');
            sourceElement.setAttribute('type', 'video/mp4');

            sourceElement.src = this.selectedMedia.preview;
            videoElement.appendChild(sourceElement);

            const fallbackText = document.createTextNode('Your browser does not support the video tag.');
            videoElement.appendChild(fallbackText);
            videoElement.load();

            const videoPreview = document.querySelector('#video-preview');

            if (videoPreview) {
                videoPreview.parentElement.removeChild(videoPreview);
            }

            document.querySelector('#video-preview-container').appendChild(videoElement);
        });
    },
    showPrevMedias() {
        const el = document.querySelector('#carousel-track');


        if (!this.qtyVisibleItems && !this.gap) {
            el.scrollTo({
                left: el.scrollLeft - (window.innerWidth < 768 ? 190 : 360),
                behavior: 'smooth'
            });

            return;
        }

        const itemWidth = el.querySelector('li').clientWidth;

        el.scrollTo({
            left: el.scrollLeft - ((itemWidth + gap) * this.qtyVisibleItems),
            behavior: 'smooth'
        });
    },
    showNextMedias() {
        const el = document.querySelector('#carousel-track');

        if (!this.qtyVisibleItems && !this.gap) {
            el.scrollTo({
                left: el.scrollLeft + (window.innerWidth < 768 ? 190 : 360),
                behavior: 'smooth'
            });

            return;
        }

        const itemWidth = el.querySelector('li').clientWidth;

        el.scrollTo({
            left: el.scrollLeft + ((itemWidth + gap) * this.qtyVisibleItems),
            behavior: 'smooth'
        });
    },
    async sendMedias() {
        this.sending = true;

        const mappedMedias = this.medias.map(media => ({
            path: media.path,
            index: media.index
        }));

        await this.$wire.call('sendMedias', mappedMedias, this.uploadType);

        this.sending = false;
        this.content = '';

        this.$dispatch('chat-message-sent');
    },
    handleMediasSaved() {
        this.medias = [];
        this.selectedMedia = null;
        this.showPrevBtn = false;
        this.showNextBtn = false;
        this.sending = false;
    },
    handleSidePanelClose() {
        this.medias
            .filter(media => !media.uploaded)
            .forEach(media => this.cancelUpload(media));

        conversationId && Echo.leave(`conversation.${conversationId}.presence`);
    },
    openPreview({type, url}) {
        this.$dispatch('show-media-preview', {type, url});
    },
    isOffline(userId) {
        return !this.isOnline(userId);
    },
    isOnline(userId) {
        return this.onlineUsers.includes(userId);
    },
    handleScrollDown() {
        this.$nextTick(() => {
            const container = document.querySelector('#chat-messages-container');

            if (container) {
                container.scrollTo(0, container.scrollHeight);
            }
        });
    },
    checkVisibility() {
        const carousel = document.querySelector('#carousel-track');
        const items = carousel.querySelectorAll('li');

        if (items.length < 1) {
            this.showPrevBtn = false;
            this.showNextBtn = false;

            return;
        }

        const firstItem = items[0];
        const lastItem = items[items.length - 1];

        const isFirstVisible = firstItem.getBoundingClientRect().left >= carousel.getBoundingClientRect().left;
        const isLastVisible = lastItem.getBoundingClientRect().right <= carousel.getBoundingClientRect().right;

        this.showPrevBtn = !isFirstVisible;
        this.showNextBtn = !isLastVisible;
    },
    init() {
        if (!conversationId) {
            return;
        }

        const channelKey = `conversation.${conversationId}.presence`;

        const storage = window.sessionStorage.getItem(channelKey);

        if (storage) {
            this.onlineUsers = JSON.parse(storage);
        }

        Echo.join(channelKey)
            .here(payload => {
                this.onlineUsers = payload.map(data => data.userId);
                window.sessionStorage.setItem(channelKey, JSON.stringify(this.onlineUsers));
            })
            .joining(payload => {
                this.onlineUsers.push(payload.userId);
                window.sessionStorage.setItem(channelKey, JSON.stringify(this.onlineUsers));
            })
            .leaving(payload => {
                this.onlineUsers = this.onlineUsers.filter(id => id !== payload.userId);
                window.sessionStorage.setItem(channelKey, JSON.stringify(this.onlineUsers));
            });

        window.addEventListener(`chat::${conversationId}::leaving`, () => {
            Echo.leave(`conversation.${conversationId}.presence`)
        });

        this.handleScrollDown();
    }
})
